import { create } from 'zustand';

const getStoredStep = () => localStorage.getItem('currentStep') || '회원약관 동의';
const getLastStoredStep = () => localStorage.getItem('currentLastStep') || '회원약관 동의';

interface TermsData {
	serviceAgree: boolean;
	privacyAgree: boolean;
}

interface BasicInfoData {
	name: string;
	enName: string;
	birth: { year: string; month: string; day: string };
	id: string;
	password: string;
	passwordCheck: string;
	gender: string;
	address: { zipcode: string; address: string; detailAddress: string };
	email: { front: string; back: string; code: string };
	cell: string;
}

interface CompanyInfoData {
	company_name?: string;
	company_name_en?: string;
	position?: string;
	md?: string;
	phone?: string;
	fax?: string;
	zipcode?: string;
	address?: string;
	detailAddress?: string;
}

interface EducationInfoData {
	school?: string;
	major?: string;
	degree?: string;
	year?: string;
	country?: string;
}

interface EtcInfoData {
	topic?: string;
	recommender?: string;
	spot: string;
}

interface SignUpInfo {
	terms: TermsData;
	basicInfo: BasicInfoData;
	companyInfo: CompanyInfoData;
	educationInfo: EducationInfoData[];
	etcInfo: EtcInfoData;
}

interface SignUpState {
	terms: TermsData;
	basicInfo: BasicInfoData;
	companyInfo: CompanyInfoData;
	educationInfo: EducationInfoData[];
	etcInfo: EtcInfoData;
	currentStep: string;
	currentLastStep: string;
	getInfo: () => SignUpInfo; // ✅ getInfo 반환 타입 지정
	updateTerms: (data: TermsData) => void;
	updateBasicInfo: (data: BasicInfoData) => void;
	updateCompanyInfo: (data: CompanyInfoData) => void;
	updateEducationInfo: (data: EducationInfoData[]) => void;
	updateEtcInfo: (data: EtcInfoData) => void;
	setStep: (step: string) => void;
	setCurrentStep: (index: string) => void;
	clearData: () => void;
	clearStep: () => void;
}

export const useSignUpStore = create<SignUpState>((set, get) => ({
	terms: { serviceAgree: false, privacyAgree: false },
	basicInfo: {
		name: '',
		enName: '',
		birth: { year: '', month: '', day: '' },
		id: '',
		password: '',
		passwordCheck: '',
		gender: '',
		address: { zipcode: '', address: '', detailAddress: '' },
		email: { front: '', back: '', code: '' },
		cell: '',
	},
	currentLastStep: '',
	companyInfo: {
		company_name: '',
		company_name_en: '',
		position: '',
		md: '',
		phone: '',
		fax: '',
		zipcode: '',
		address: '',
		detailAddress: '',
	},
	educationInfo: [],
	etcInfo: { topic: '', recommender: '', spot: '' },
	currentStep: getStoredStep(),
	curentLastStep: getLastStoredStep(),
	updateTerms: (data: TermsData) =>
		set(() => ({
			terms: data, // ✅ 완전한 배열을 받도록 타입을 수정
		})),

	updateBasicInfo: (data: BasicInfoData) => set(() => ({ basicInfo: data })),
	updateCompanyInfo: (data: CompanyInfoData) => set(() => ({ companyInfo: data })),
	updateEducationInfo: (data: EducationInfoData[]) =>
		set(() => ({
			educationInfo: data, // ✅ 완전한 배열을 받도록 타입을 수정
		})),

	updateEtcInfo: (data: EtcInfoData) => set(() => ({ etcInfo: data })),

	// ✅ getInfo 반환 타입 지정
	getInfo: (): SignUpInfo => {
		const state = get();
		return {
			terms: state.terms,
			basicInfo: state.basicInfo,
			companyInfo: state.companyInfo,
			educationInfo: state.educationInfo,
			etcInfo: state.etcInfo,
		};
	},

	setStep: (step) => {
		localStorage.setItem('currentStep', step);
		set({ currentStep: step });
	},
	setCurrentStep: (index) => {
		localStorage.setItem('currentLastStep', index);
		set({ currentLastStep: index });
	},
	clearStep: () => {
		localStorage.removeItem('currentStep');
		localStorage.removeItem('currentLastStep');
		set({ currentStep: '회원약관 동의', currentLastStep: '회원약관 동의' });
	},
	clearData: () =>
		set({
			terms: { serviceAgree: false, privacyAgree: false }, // 초기값 설정
			basicInfo: {
				name: '',
				enName: '',
				birth: { year: '', month: '', day: '' },
				id: '',
				password: '',
				passwordCheck: '',
				gender: '',
				address: { zipcode: '', address: '', detailAddress: '' },
				email: { front: '', back: '', code: '' },
				cell: '',
			}, // 초기값 설정
			companyInfo: {
				company_name: '',
				company_name_en: '',
				position: '',
				md: '',
				phone: '',
				fax: '',
				zipcode: '',
				address: '',
				detailAddress: '',
			}, // 초기값 설정
			educationInfo: [], // 초기값 설정
			etcInfo: { topic: '', recommender: '', spot: '' }, // 초기값 설정
		}),
}));
